/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { applyMiddleware, createStore, AnyAction, Action, Store } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk, { ThunkAction } from 'redux-thunk';
import { composeWithDevTools } from '@redux-devtools/extension';

import reducers from './reducers';
import DataCache from './utils/DataCache';
/*
 * TODO - still need to consolidate all reducers between existing
 * ICS root reducer strategy/location above and AS root reducer
 * strategy in /Common below:
 */
// import rootReducer from './Common/reducers/index';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['tenant'], // only the tenant and will be preserved
};

const persistedReducer = persistReducer(persistConfig, reducers);
const middleware = applyMiddleware(thunk.withExtraArgument({ dataCache: new DataCache() }));

/**
 * Function to create a redux store using our reducers, you can use for your testcases if you need a real reducer
 * @param {object} defaultState
 * @param {func} reducers optional reducers parameter, by default uses our full reducers
 */
export const createStoreWithReducers = (
  defaultState: unknown,
  reducer = reducers
): Store<Record<string, unknown>, Action> =>
  createStore(reducer, defaultState, composeWithDevTools(middleware));

// Our default store is using the persisted reducer
export const store = createStoreWithReducers({}, persistedReducer);
export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

// This type is for the middleware given to Redux action creator functions
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  { dataCache: DataCache },
  AnyAction
>;
